/**
 * Suivi Google Analytics 4 (gtag.js) pour FlashCV.
 * Aucune donnée personnelle (nom, email, téléphone, contenu du CV...) ne doit
 * jamais être passée en paramètre : uniquement des identifiants techniques et
 * des catégories. Le user_id GA (voir setAnalyticsUserId) utilise l'_id
 * backend, déjà un identifiant opaque non-PII.
 */

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
    }
}

/** Un item du tableau "items" attendu par les rapports GA4 Monétisation. */
export interface GaItem {
    item_id: string;
    item_name: string;
    item_category: string;
    price: number;
    quantity: number;
}

type EventParams = Record<string, string | number | boolean | GaItem[]>;

const isProd = process.env.NODE_ENV === 'production';
const debugMode = process.env.REACT_APP_GA_DEBUG === 'true';

function send(...args: unknown[]): void {
    try {
        if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
        if (!isProd && !debugMode) {
            // eslint-disable-next-line no-console
            console.log('[analytics]', ...args);
            return;
        }
        window.gtag(...args);
    } catch {
        // le tracking ne doit jamais faire planter l'app
    }
}

/** Envoie un événement GA4. Ne jamais passer de données personnelles dans params. */
export function trackEvent(name: string, params: EventParams = {}): void {
    send('event', name, debugMode ? { ...params, debug_mode: true } : params);
}

/**
 * À appeler à chaque changement de route (gtag ne le fait pas automatiquement
 * en SPA). Pas de page_path : GA4 le dérive déjà de page_location, l'envoyer
 * en plus consommerait un slot de dimension personnalisée pour rien.
 */
export function trackPageView(path: string): void {
    let location = path;
    let title = '';
    try {
        location = window.location.href;
        title = document.title;
    } catch {
        /* environnement sans window/document */
    }
    trackEvent('page_view', { page_location: location, page_title: title });
}

/**
 * Mécanisme user_id natif de GA4 (pas un paramètre custom). _id est déjà un
 * identifiant opaque non-PII émis par le backend, inutile de le hasher.
 * À appeler après connexion (et à la réhydratation de session) ; remettre à
 * null à la déconnexion.
 */
export function setAnalyticsUserId(userId: string | null): void {
    send('set', 'user_id', userId);
}

const PURCHASE_KEY_PREFIX = 'ga_purchase_sent:';
const PURCHASE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Retire les marqueurs de plus de 7 jours pour ne pas faire grossir localStorage indéfiniment. */
function purgeOldTrackedPurchases(): void {
    const now = Date.now();
    for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(PURCHASE_KEY_PREFIX)) continue;
        const sentAt = Number(localStorage.getItem(key));
        if (!sentAt || now - sentAt > PURCHASE_TTL_MS) {
            localStorage.removeItem(key);
        }
    }
}

/**
 * Envoie "purchase" au plus une fois par transaction_id.
 *
 * localStorage (pas sessionStorage) : les paiements mobile money redirigent
 * souvent vers une page externe puis reviennent dans un nouvel onglet ou
 * après un rechargement complet, ce qui vide sessionStorage — exactement le
 * scénario où le garde-fou anti-doublon doit tenir. Le polling de statut
 * Nafolo peut aussi voir "completed" sur plusieurs itérations, et un remount
 * du composant de paiement peut relancer la détection.
 */
export function trackPurchaseOnce(transactionId: string, params: EventParams): void {
    const key = PURCHASE_KEY_PREFIX + transactionId;
    try {
        purgeOldTrackedPurchases();
        if (localStorage.getItem(key)) return;
        localStorage.setItem(key, String(Date.now()));
    } catch {
        // localStorage indisponible : pas de déduplication possible, on envoie quand même
    }
    trackEvent('purchase', { transaction_id: transactionId, ...params });
}
